import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Flex, Tooltip, Typography } from "../ui";
import React from "react";
import { spacing } from "../layout/spacing";

export function RepeatableRemoveButton(props: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Tooltip title={props.label}>
      <span style={{ display: "inline-flex", flexShrink: 0 }}>
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          aria-label={props.label}
          onClick={props.onClick}
          disabled={props.disabled}
        />
      </span>
    </Tooltip>
  );
}

export function RepeatableAddButton(props: {
  label: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  block?: boolean;
  size?: "small" | "middle";
}) {
  return (
    <Button
      type="dashed"
      icon={<PlusOutlined />}
      onClick={props.onClick}
      disabled={props.disabled}
      block={props.block}
      size={props.size}
    >
      {props.label}
    </Button>
  );
}

export interface RepeatableRowsProps {
  addLabel: React.ReactNode;
  onAdd: () => void;
  disabled?: boolean;
  emptyText?: React.ReactNode;
  children?: React.ReactNode;
}

/**
 * Kompakte wiederholbare Zeilen: Felder in einer Reihe, Icon-Papierkorb, gestricheltes Plus.
 * Chrome liegt nur hier — Aufrufer liefern Inhalt, Beschriftungen und onAdd/onRemove.
 */
export function RepeatableRows({ addLabel, onAdd, disabled, emptyText, children }: RepeatableRowsProps) {
  const items = React.Children.toArray(children);
  return (
    <div>
      {items.length === 0 && emptyText != null ? (
        <div style={{ marginBottom: spacing.elementGap }}>
          <Typography.Text type="secondary">{emptyText}</Typography.Text>
        </div>
      ) : null}
      {items.length > 0 ? <div style={{ marginBottom: spacing.elementGap }}>{children}</div> : null}
      <RepeatableAddButton label={addLabel} onClick={onAdd} disabled={disabled} size="small" />
    </div>
  );
}

export interface RepeatableRowProps {
  removeLabel: string;
  onRemove?: () => void;
  disabled?: boolean;
  extra?: React.ReactNode;
  children: React.ReactNode | ((removeButton: React.ReactNode) => React.ReactNode);
}

export function RepeatableRow({ removeLabel, onRemove, disabled, extra, children }: RepeatableRowProps) {
  const removeButton =
    onRemove == null ? null : (
      <RepeatableRemoveButton label={removeLabel} onClick={onRemove} disabled={disabled} />
    );
  const actions = (
    <>
      {extra}
      {removeButton}
    </>
  );
  if (typeof children === "function") {
    return <>{children(actions)}</>;
  }
  return (
    <Flex align="center" gap={spacing.tightGap} wrap="wrap" style={{ marginBottom: spacing.tightGap }}>
      {children}
      {actions}
    </Flex>
  );
}
